import {
  IndentDecrease,
  IndentIncrease,
  Link2,
  List,
  ListOrdered,
  Quote,
} from "lucide-react";
import { t } from "i18next";
import { useState } from "react";
import type React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { isSafeUrl } from "@/lib/utils/urlUtils";

interface FormattingToolbarProps {
  isBold: boolean;
  isItalic: boolean;
  isLink: boolean;
  isCode: boolean;
  isBlockquote: boolean;
  isHeading1: boolean;
  isHeading2: boolean;
  isUnorderedList: boolean;
  isOrderedList: boolean;
  onToggleBold: () => void;
  onToggleItalic: () => void;
  onApplyLink: (url: string) => void;
  onRemoveLink: () => void;
  onBeforeLinkEdit?: () => void;
  onToggleCode: () => void;
  onToggleBlockquote: () => void;
  onToggleHeading1: () => void;
  onToggleHeading2: () => void;
  onToggleUnorderedList: () => void;
  onToggleOrderedList: () => void;
  onIndent: () => void;
  onOutdent: () => void;
  onAttachClick?: () => void;
  fileInputRef?: React.RefObject<HTMLInputElement | null>;
  onFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showAttachButton?: boolean;
  emojiPicker?: React.ReactNode;
  onLinkPopoverOpenChange?: (open: boolean) => void;
}

const ToolbarTooltip: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <Tooltip disableHoverableContent>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

const TOOLBAR_BUTTON_CLASS =
  "relative z-10 size-8 p-0 text-sm leading-none max-[1399px]:size-7 max-[1399px]:text-xs";

export const FormattingToolbar: React.FC<FormattingToolbarProps> = ({
  isBold,
  isItalic,
  isLink,
  isHeading1,
  isHeading2,
  isBlockquote,
  isUnorderedList,
  isOrderedList,
  onToggleBold,
  onToggleItalic,
  onApplyLink,
  onRemoveLink,
  onBeforeLinkEdit,
  onToggleHeading1,
  onToggleHeading2,
  onToggleBlockquote,
  onToggleUnorderedList,
  onToggleOrderedList,
  onIndent,
  onOutdent,
  onAttachClick,
  fileInputRef,
  onFileChange,
  showAttachButton = true,
  emojiPicker,
  onLinkPopoverOpenChange,
}) => {
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [linkValue, setLinkValue] = useState("");
  const [linkError, setLinkError] = useState<string | null>(null);

  const handleLinkPopoverOpenChange = (open: boolean) => {
    setLinkPopoverOpen(open);
    onLinkPopoverOpenChange?.(open);
  };

  const normalizeUrl = (raw: string): string => {
    const value = raw.trim();
    if (!value) return "";
    const hasProtocol = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(value);
    return hasProtocol ? value : `https://${value}`;
  };

  const handleApplyLink = () => {
    const normalized = normalizeUrl(linkValue);
    if (!isSafeUrl(normalized)) {
      setLinkError(t("common.invalidUrl"));
      return;
    }

    onApplyLink(normalized);
    handleLinkPopoverOpenChange(false);
    setLinkValue("");
    setLinkError(null);
  };

  const closeLinkPopover = () => {
    handleLinkPopoverOpenChange(false);
    setLinkValue("");
    setLinkError(null);
  };

  const preserveEditorCaret = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="relative flex w-max min-w-max max-w-full items-center pr-1">
        <div className="order-3 flex items-center rounded-md bg-[#ebf0f2]">
          {/* Bold Button */}
          <ToolbarTooltip label={t("common.bold")}>
            <Button
              variant={isBold ? "formattingActive" : "formattingInactive"}
              size="formatting"
              className={`${TOOLBAR_BUTTON_CLASS} order-3 font-bold`}
              onClick={onToggleBold}
              onMouseDown={preserveEditorCaret}
              type="button"
              aria-label={t("common.bold")}
            >
              B
            </Button>
          </ToolbarTooltip>

          {/* Italic Button */}
          <ToolbarTooltip label={t("common.italic")}>
            <Button
              variant={isItalic ? "formattingActive" : "formattingInactive"}
              size="formatting"
              className={`${TOOLBAR_BUTTON_CLASS} order-4 italic`}
              onClick={onToggleItalic}
              onMouseDown={preserveEditorCaret}
              type="button"
              aria-label={t("common.italic")}
            >
              I
            </Button>
          </ToolbarTooltip>

          {/* Link Button */}
          {isLink ? (
            <ToolbarTooltip label={t("common.remove")}>
              <Button
                variant="formattingActive"
                size="formatting"
                className={`${TOOLBAR_BUTTON_CLASS} order-5`}
                onClick={onRemoveLink}
                type="button"
                aria-label={t("common.remove")}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onBeforeLinkEdit?.();
                }}
              >
                <Link2 className="size-4" />
              </Button>
            </ToolbarTooltip>
          ) : (
            <Popover
              open={linkPopoverOpen}
              onOpenChange={(open) => {
                handleLinkPopoverOpenChange(open);
                if (!open) {
                  setLinkValue("");
                  setLinkError(null);
                }
              }}
            >
              <ToolbarTooltip label={t("common.addLink")}>
                <PopoverTrigger asChild>
                  <Button
                    variant="formattingInactive"
                    size="formatting"
                    className={`${TOOLBAR_BUTTON_CLASS} order-5`}
                    type="button"
                    aria-label={t("common.addLink")}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onBeforeLinkEdit?.();
                    }}
                  >
                    <Link2 className="size-4" />
                  </Button>
                </PopoverTrigger>
              </ToolbarTooltip>
              <PopoverContent className="w-72 space-y-2 p-3" align="start">
                <Input
                  value={linkValue}
                  onChange={(e) => {
                    setLinkValue(e.target.value);
                    if (linkError) {
                      setLinkError(null);
                    }
                  }}
                  placeholder="https://example.com"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleApplyLink();
                    }
                  }}
                />
                {linkError && (
                  <p className="text-xs text-red-500">{linkError}</p>
                )}
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={closeLinkPopover}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    variant="submit"
                    size="sm"
                    type="button"
                    onClick={handleApplyLink}
                  >
                    {t("common.apply")}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>

        <div className="order-6 ml-4 flex items-center rounded-md bg-[#ebf0f2] max-[1399px]:ml-1.5">
          {/* Large Heading Button */}
          <ToolbarTooltip label={t("common.headingLarge")}>
            <Button
              variant={isHeading1 ? "formattingActive" : "formattingInactive"}
              size="formatting"
              className={`${TOOLBAR_BUTTON_CLASS} order-6`}
              onClick={onToggleHeading1}
              onMouseDown={preserveEditorCaret}
              type="button"
              aria-label={t("common.headingLarge")}
            >
              H1
            </Button>
          </ToolbarTooltip>

          {/* Small Heading Button */}
          <ToolbarTooltip label={t("common.headingSmall")}>
            <Button
              variant={isHeading2 ? "formattingActive" : "formattingInactive"}
              size="formatting"
              className={`${TOOLBAR_BUTTON_CLASS} order-7`}
              onClick={onToggleHeading2}
              onMouseDown={preserveEditorCaret}
              type="button"
              aria-label={t("common.headingSmall")}
            >
              H2
            </Button>
          </ToolbarTooltip>

          {/* Code Button */}
          {/* <Button
        variant={isCode ? "formattingActive" : "formattingInactive"}
        size="formatting"
        onClick={onToggleCode}
        type="button"
        aria-label={t("common.code")}
      >
        &lt;&gt;
      </Button> */}

          {/* Blockquote Button */}
          <ToolbarTooltip label={t("common.blockquote")}>
            <Button
              variant={isBlockquote ? "formattingActive" : "formattingInactive"}
              size="formatting"
              className={`${TOOLBAR_BUTTON_CLASS} order-8`}
              onClick={onToggleBlockquote}
              onMouseDown={preserveEditorCaret}
              type="button"
              aria-label={t("common.blockquote")}
            >
              <Quote className="size-4" />
            </Button>
          </ToolbarTooltip>

          <span
            aria-hidden="true"
            className="bg-rw-dark-gray/20 order-9 mx-1 h-5 w-px max-[1399px]:mx-0.5"
          />

          <ToolbarTooltip
            label={t("common.bulletList", { defaultValue: "Bullet list" })}
          >
            <Button
              variant={
                isUnorderedList ? "formattingActive" : "formattingInactive"
              }
              size="formatting"
              className={`${TOOLBAR_BUTTON_CLASS} order-9`}
              onClick={onToggleUnorderedList}
              onMouseDown={preserveEditorCaret}
              type="button"
            >
              <List className="size-4" />
            </Button>
          </ToolbarTooltip>
          <ToolbarTooltip
            label={t("common.numberedList", { defaultValue: "Numbered list" })}
          >
            <Button
              variant={
                isOrderedList ? "formattingActive" : "formattingInactive"
              }
              size="formatting"
              className={`${TOOLBAR_BUTTON_CLASS} order-10`}
              onClick={onToggleOrderedList}
              onMouseDown={preserveEditorCaret}
              type="button"
            >
              <ListOrdered className="size-4" />
            </Button>
          </ToolbarTooltip>
          <ToolbarTooltip
            label={
              isUnorderedList || isOrderedList
                ? t("common.increaseIndent", {
                    defaultValue: "Increase indent",
                  })
                : t("common.indentRequiresList")
            }
          >
            <span className="order-11 inline-flex">
              <Button
                variant="formattingInactive"
                size="formatting"
                className={`${TOOLBAR_BUTTON_CLASS} ${!isUnorderedList && !isOrderedList ? "pointer-events-none" : ""}`}
                onClick={onIndent}
                onMouseDown={preserveEditorCaret}
                type="button"
                disabled={!isUnorderedList && !isOrderedList}
              >
                <IndentIncrease className="size-4" />
              </Button>
            </span>
          </ToolbarTooltip>
          <ToolbarTooltip
            label={
              isUnorderedList || isOrderedList
                ? t("common.decreaseIndent", {
                    defaultValue: "Decrease indent",
                  })
                : t("common.indentRequiresList")
            }
          >
            <span className="order-12 inline-flex">
              <Button
                variant="formattingInactive"
                size="formatting"
                className={`${TOOLBAR_BUTTON_CLASS} ${!isUnorderedList && !isOrderedList ? "pointer-events-none" : ""}`}
                onClick={onOutdent}
                onMouseDown={preserveEditorCaret}
                type="button"
                disabled={!isUnorderedList && !isOrderedList}
              >
                <IndentDecrease className="size-4" />
              </Button>
            </span>
          </ToolbarTooltip>
        </div>

        <span className="mr-2 flex size-8 items-center justify-center rounded-md bg-[#ebf0f2] max-[1399px]:mr-1.5 max-[1399px]:size-7">
          {emojiPicker}
        </span>

        {/* File Attach Button */}
        {showAttachButton && onAttachClick && (
          <>
            <ToolbarTooltip label={t("common.attach")}>
              <Button
                variant="formattingInactive"
                size="formatting"
                className={`${TOOLBAR_BUTTON_CLASS} -order-1 mr-2 rounded-md bg-[#ebf0f2] max-[1399px]:mr-1.5`}
                onClick={onAttachClick}
                type="button"
                aria-label={t("common.attach")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  aria-hidden="true"
                >
                  <path d="M4.406 3.342A5.53 5.53 0 0 1 8 2c2.69 0 4.923 2 5.166 4.579C14.758 6.804 16 8.137 16 9.773c0 1.796-1.498 3.227-3.313 3.227H4.781C2.708 13 1 11.366 1 9.318c0-1.763 1.266-3.223 2.942-3.593.143-.863.698-1.723 1.464-2.383zM8 10.5a.5.5 0 0 0 .5-.5V6.707l1.146 1.147a.5.5 0 0 0 .708-.708l-2-2a.5.5 0 0 0-.708 0l-2 2a.5.5 0 1 0 .708.708L7.5 6.707V10a.5.5 0 0 0 .5.5z" />
                </svg>
              </Button>
            </ToolbarTooltip>
            {fileInputRef && onFileChange && (
              <input
                type="file"
                ref={fileInputRef}
                onChange={onFileChange}
                className="hidden"
                multiple
              />
            )}
          </>
        )}
      </div>
    </TooltipProvider>
  );
};
